import { useEffect, useState } from "react";
import {
  LuChevronLeft,
  LuChevronRight,
  LuChevronsLeft,
  LuChevronsRight,
} from "react-icons/lu";

const buildPageList = (page, totalPages, siblings) => {
  const pages = [];
  for (let p = 1; p <= totalPages; p++) {
    if (p === 1 || p === totalPages || Math.abs(p - page) <= siblings) {
      if (pages.length && p - pages[pages.length - 1] > 1) pages.push("…");
      pages.push(p);
    }
  }
  return pages;
};

const iconBtn =
  "h-9 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 " +
  "transition hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700 " +
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 " +
  "disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-gray-200 disabled:hover:bg-white disabled:hover:text-gray-500";

const PageNumbers = ({ page, totalPages, siblings, onChange, disabled, className }) => (
  <div className={`items-center gap-1 ${className}`}>
    {buildPageList(page, totalPages, siblings).map((p, i) =>
      p === "…" ? (
        <span key={`gap-${i}`} className="w-6 select-none text-center text-[12px] text-gray-400">
          …
        </span>
      ) : (
        <button
          key={p}
          type="button"
          onClick={() => onChange(p)}
          disabled={disabled}
          aria-label={`Page ${p}`}
          aria-current={p === page ? "page" : undefined}
          className={`flex h-9 min-w-[36px] items-center justify-center rounded-lg px-2 text-[12.5px] font-semibold tabular-nums transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 ${
            p === page
              ? "bg-brand-gradient text-white shadow-brand"
              : "text-gray-600 hover:bg-brand-50 hover:text-brand-700"
          }`}
        >
          {p}
        </button>
      ),
    )}
  </div>
);

/**
 * Responsive pagination bar.
 *  - phone:   summary on top, then  ‹ Prev · 3 / 10 · Next ›  with large tap targets
 *  - tablet:  numbered pages (current ±1)
 *  - desktop: first/last buttons, current ±2, and a "Go to" page box
 */
export default function Pagination({
  page,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 50],
  itemLabel = "items",
  extra = null,
  loading = false,
  className = "",
}) {
  const [jump, setJump] = useState("");
  useEffect(() => setJump(""), [page]);

  if (!totalItems) return null;

  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, totalItems);
  const hasPages = totalPages > 1;
  const go = (p) => {
    if (loading || p < 1 || p > totalPages || p === page) return;
    onPageChange(p);
  };

  const submitJump = (e) => {
    e.preventDefault();
    const p = parseInt(jump, 10);
    if (!Number.isNaN(p)) go(Math.min(Math.max(1, p), totalPages));
  };

  return (
    <nav
      aria-label="Pagination"
      className={`mt-4 rounded-xl border border-gray-200 bg-white px-3 py-3 shadow-sm sm:px-4 ${className}`}
    >
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        {/* Summary + page size */}
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 lg:justify-start">
          <p className="text-[12px] text-gray-500">
            Showing{" "}
            <span className="font-semibold tabular-nums text-gray-800">
              {start}–{end}
            </span>{" "}
            of <span className="font-semibold tabular-nums text-gray-800">{totalItems}</span>{" "}
            {itemLabel}
            {extra && <span className="text-gray-400"> · {extra}</span>}
          </p>

          {onPageSizeChange && (
            <label className="flex items-center gap-2 text-[12px] text-gray-500">
              <span className="hidden sm:inline">Per page</span>
              <select
                value={pageSize}
                onChange={(e) => onPageSizeChange(Number(e.target.value))}
                disabled={loading}
                aria-label="Items per page"
                className="h-8 cursor-pointer rounded-lg border border-gray-200 bg-gray-50 px-2 text-[12px] font-medium text-gray-700 outline-none transition hover:border-brand-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
              >
                {pageSizeOptions.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>

        {hasPages && (
          <div className="flex items-center justify-between gap-2 sm:justify-center lg:justify-end">
            {/* First */}
            <button type="button" onClick={() => go(1)} disabled={page <= 1 || loading} aria-label="First page" className={`${iconBtn} hidden w-9 lg:flex`}>
              <LuChevronsLeft size={15} />
            </button>

            {/* Prev: labelled button on phones, icon from tablet up */}
            <button
              type="button"
              onClick={() => go(page - 1)}
              disabled={page <= 1 || loading}
              aria-label="Previous page"
              className={`${iconBtn} flex flex-1 gap-1 px-3 text-[12.5px] font-medium sm:w-9 sm:flex-none sm:px-0`}
            >
              <LuChevronLeft size={15} />
              <span className="sm:hidden">Prev</span>
            </button>

            {/* Phone: compact indicator */}
            <span className="flex h-9 shrink-0 items-center rounded-lg bg-brand-50 px-3 text-[12.5px] font-semibold tabular-nums text-brand-700 sm:hidden">
              {page} / {totalPages}
            </span>

            {/* Tablet: current ±1 · Desktop: current ±2 */}
            <PageNumbers page={page} totalPages={totalPages} siblings={1} onChange={go} disabled={loading} className="hidden sm:flex lg:hidden" />
            <PageNumbers page={page} totalPages={totalPages} siblings={2} onChange={go} disabled={loading} className="hidden lg:flex" />

            {/* Next */}
            <button
              type="button"
              onClick={() => go(page + 1)}
              disabled={page >= totalPages || loading}
              aria-label="Next page"
              className={`${iconBtn} flex flex-1 gap-1 px-3 text-[12.5px] font-medium sm:w-9 sm:flex-none sm:px-0`}
            >
              <span className="sm:hidden">Next</span>
              <LuChevronRight size={15} />
            </button>

            {/* Last */}
            <button type="button" onClick={() => go(totalPages)} disabled={page >= totalPages || loading} aria-label="Last page" className={`${iconBtn} hidden w-9 lg:flex`}>
              <LuChevronsRight size={15} />
            </button>

            {/* Jump to page (desktop, only worth it with many pages) */}
            {totalPages > 7 && (
              <form onSubmit={submitJump} className="ml-2 hidden items-center gap-1.5 border-l border-gray-200 pl-3 xl:flex">
                <span className="text-[12px] text-gray-500">Go to</span>
                <input
                  type="number"
                  min={1}
                  max={totalPages}
                  value={jump}
                  onChange={(e) => setJump(e.target.value)}
                  placeholder={String(page)}
                  aria-label="Go to page"
                  className="h-8 w-14 rounded-lg border border-gray-200 px-2 text-center text-[12px] tabular-nums outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
                />
              </form>
            )}
          </div>
        )}
      </div>
    </nav>
  );
}
